import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Element as ElementType } from '../types';
import { getRandomWord } from '@/lib/api';
import { useToast } from '@/hooks/use-sonner-toast';
import { Check, Target, Loader2, Trash2 } from 'lucide-react';
import { useDrop } from 'react-dnd';
import { Skeleton } from './ui/skeleton';
import { useInteractionMode } from '../contexts/InteractionModeContext';
import { useGameState } from '@/contexts/GameStateContext';
import { useActions } from '@/contexts/ActionContext';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { trackGoalCompletion } from '@/lib/firebase/functions';
import { getAuth } from 'firebase/auth';
import { Confetti, ConfettiRef } from './ui/confetti';

interface GoalPanelProps {
  droppedElement: ElementType | null;
  skipMode: boolean;
  onSubmit: () => void;
  onPointsEarned: (points: number) => void;
  onSkipUsed: (
    goalType: 'Item' | 'Location' | 'Character',
    currentWord?: string,
    recentWords?: string[]
  ) => Promise<string>;
}

interface GoalItem {
  type: 'Item' | 'Location' | 'Character';
  word: string;
  matched: boolean;
}

const GoalPanel: React.FC<GoalPanelProps> = ({
  droppedElement,
  skipMode,
  onSubmit,
  onPointsEarned,
  onSkipUsed,
}) => {
  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();
  const { currentUser } = useAuth();
  const { useGameAction } = useActions();
  const {
    goals: gameStateGoals,
    setGoals: setGameStateGoals,
    isLoading: gameStateLoading,
    wordHistory,
    addToWordHistory,
    incrementStat,
    skipCount,
  } = useGameState();

  // Add confetti ref
  const confettiRef = useRef<ConfettiRef>(null);

  const [goals, setGoals] = useState<GoalItem[]>([
    { type: 'Item', word: '', matched: false },
    { type: 'Location', word: '', matched: false },
    { type: 'Character', word: '', matched: false },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMatched, setIsMatched] = useState(false);
  const [matchedIndex, setMatchedIndex] = useState<number | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const { toast } = useToast();

  // Handle a match between an element and a goal
  const handleMatch = (index: number, _element: ElementType) => {
    // The element parameter is not used but kept for clarity
    setIsMatched(true);
    setMatchedIndex(index);
  };

  // Handle tap destination for goal panel
  const handleTapDestination = () => {
    if (mode === 'tap-to-location' && selectedElement) {
      // Check if the element matches any of the goals
      const index = goals.findIndex((goal) => {
        if (goal.matched || !goal.word) return false;

        // Normalize both strings: trim whitespace, convert to lowercase
        const normalizedGoal = goal.word.trim().toLowerCase();
        const normalizedElement = selectedElement.name.trim().toLowerCase();

        // Check for exact match first
        if (normalizedGoal === normalizedElement) return true;

        // Check for match with 'The ' prefix removed (for locations like 'The White House' vs 'White House')
        if (normalizedGoal.startsWith('the ') && normalizedElement === normalizedGoal.substring(4))
          return true;
        if (
          normalizedElement.startsWith('the ') &&
          normalizedGoal === normalizedElement.substring(4)
        )
          return true;

        return false;
      });

      if (index !== -1) {
        // Handle the match
        handleMatch(index, selectedElement);
        setSelectedElement(null);
        // Ensure we're not shaking on a match
        setIsShaking(false);
      } else if (!skipMode) {
        // If there's no match, trigger the shake animation
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    }
  };

  // Set up drop functionality for elements
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'element',
    drop: (item: any) => {
      // Extract the element from the item (which might have additional properties)
      const element: ElementType = {
        id: item.id,
        name: item.name,
        type: item.type,
        color: item.color,
        // Remove the discovered property as it's not in the ElementType interface
      };

      // Check if the dropped element matches any of the goals
      const index = goals.findIndex((goal) => {
        if (goal.matched || !goal.word) return false;

        // Normalize both strings: trim whitespace, convert to lowercase
        const normalizedGoal = goal.word.trim().toLowerCase();
        const normalizedElement = element.name.trim().toLowerCase();

        // Check for exact match first
        if (normalizedGoal === normalizedElement) return true;

        // Check for match with 'The ' prefix removed (for locations like 'The White House' vs 'White House')
        if (normalizedGoal.startsWith('the ') && normalizedElement === normalizedGoal.substring(4))
          return true;
        if (
          normalizedElement.startsWith('the ') &&
          normalizedGoal === normalizedElement.substring(4)
        )
          return true;

        return false;
      });

      // If there's no match and we're not in skip mode, trigger the shake animation
      if (index === -1 && !skipMode) {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }

      // If there's a match, let the parent component handle it
      // Don't shake if there's a match
      if (index !== -1) {
        setIsShaking(false);
      }

      return { name: 'GoalPanel', handled: true, matched: index !== -1 };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    // Prevent text selection during drag operations
    options: {
      dropEffect: 'copy',
    },
  }));

  // Track if we've already loaded initial goals
  const initialGoalsLoadedRef = useRef(false);

  // Load initial goals
  useEffect(() => {
    // Skip if we've already processed the initial goals
    if (initialGoalsLoadedRef.current) {
      return;
    }

    // If game state is still loading, wait for it
    if (gameStateLoading) {
      return;
    }

    const loadInitialGoals = async () => {
      setIsLoading(true);

      // If we have goals in the game state, use those
      if (gameStateGoals && gameStateGoals.length > 0) {
        setGoals(
          gameStateGoals.map((goal) => ({
            type: goal.type,
            word: goal.word,
            matched: goal.matched,
          }))
        );
        initialGoalsLoadedRef.current = true;
        setIsLoading(false);
        return;
      }

      // Start with empty goals until API call

      setGoals([
        { type: 'Item', word: '', matched: false },
        { type: 'Location', word: '', matched: false },
        { type: 'Character', word: '', matched: false },
      ]);
      setIsLoading(false);
    };

    loadInitialGoals();
  }, [gameStateGoals, gameStateLoading]); // Run when game state goals or loading state changes

  // Track if we've already loaded words for this session
  const initialWordsLoadedRef = useRef(false);

  // Load words only for new users with no goals
  useEffect(() => {
    // Only run this once when the component mounts
    if (initialWordsLoadedRef.current) {
      return;
    }

    // If game state is still loading, wait for it
    if (gameStateLoading) {
      return;
    }

    const loadWords = async () => {
      // Only load words if we're in the forge and logged in
      if (!currentUser || window.location.pathname !== '/forge') {
        return;
      }

      // If we have goals in the game state with words, don't fetch new ones
      if (gameStateGoals && gameStateGoals.length > 0 && gameStateGoals.some((goal) => goal.word)) {
        initialWordsLoadedRef.current = true;
        return;
      }

      // Only fetch new words if we don't have any AND we're a new user
      // This should only happen for brand new users with no goals
      if (goals.every((goal) => !goal.word) && (!gameStateGoals || gameStateGoals.length === 0)) {
        try {
          const newGoals = await Promise.all(
            goals.map(async (goal) => {
              // Get a new word, excluding words in the history
              const newWord = await getRandomWord(goal.type, '', wordHistory[goal.type]);

              // Add the new word to the history
              addToWordHistory(goal.type, newWord);

              return {
                ...goal,
                word: newWord,
              };
            })
          );

          setGoals(newGoals);

          // Update the game state goals
          const gameStateGoalsToSave = newGoals.map((goal) => ({
            id: Math.random().toString(36).substring(2, 9),
            type: goal.type,
            word: goal.word,
            matched: goal.matched,
          }));

          setGameStateGoals(gameStateGoalsToSave);
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to load goals. Please try again.',
            variant: 'destructive',
          });
        }
      } else {
      }

      // Mark as loaded regardless of outcome
      initialWordsLoadedRef.current = true;
    };

    loadWords();
  }, [currentUser, gameStateGoals, gameStateLoading, goals]); // Run when user, goals, or loading state changes

  // Handle skip mode
  useEffect(() => {
    if (!skipMode) return;

    // No need to show a toast here as it's now shown in the Index component
  }, [skipMode]);

  // Check for matches when a new element is dropped
  useEffect(() => {
    if (!droppedElement) {
      setIsMatched(false);
      setMatchedIndex(null);
      return;
    }

    // Check if the dropped element matches any of the goals
    // Use a more flexible comparison to handle slight variations
    const index = goals.findIndex((goal) => {
      if (goal.matched || !goal.word) return false;

      // Normalize both strings: trim whitespace, convert to lowercase
      const normalizedGoal = goal.word.trim().toLowerCase();
      const normalizedElement = droppedElement.name.trim().toLowerCase();

      // Check for exact match first
      if (normalizedGoal === normalizedElement) return true;

      // Check for match with 'The ' prefix removed (for locations like 'The White House' vs 'White House')
      if (normalizedGoal.startsWith('the ') && normalizedElement === normalizedGoal.substring(4))
        return true;
      if (normalizedElement.startsWith('the ') && normalizedGoal === normalizedElement.substring(4))
        return true;

      return false;
    });

    if (index !== -1) {
      setIsMatched(true);
      setMatchedIndex(index);
      // Explicitly ensure we're not shaking on a match
      setIsShaking(false);
    } else {
      setIsMatched(false);
      setMatchedIndex(null);
      // We now handle shaking in the useDrop function
      // so we don't need to trigger it here
    }
  }, [droppedElement, goals]);

  // Handle submit button click
  const handleSubmit = async () => {
    if (!isMatched || matchedIndex === null) return;

    // Use an action for completing a goal
    const actionResult = await useGameAction('goal', goals[matchedIndex].type);

    if (!actionResult.success) {
      toast({
        title: 'Action Failed',
        description: actionResult.message || 'You need more actions to complete this goal.',
        variant: 'destructive',
      });
      return;
    }

    // Update the matched goal
    const newGoals = [...goals];
    newGoals[matchedIndex].matched = true;

    // Set loading state for the matched goal
    newGoals[matchedIndex] = {
      ...newGoals[matchedIndex],
      word: '',
      matched: true,
    };
    setGoals(newGoals);

    // Update the game state goals

    const updatedGameStateGoals = gameStateGoals.map((goal, idx) => {
      if (idx === matchedIndex) {
        return {
          ...goal,
          id: goal.id, // Explicitly preserve the ID
          matched: true,
        };
      }
      return goal;
    });

    setGameStateGoals(updatedGameStateGoals);

    // Award points (50 points per match)
    onPointsEarned(50);

    // Increment the goalsCompleted stat
    incrementStat('goalsCompleted');

    // Track goal completion in Firebase
    try {
      // Only track goal completion if user is authenticated
      if (currentUser) {
        // Get the current auth token to ensure it's fresh
        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
          // Force token refresh before calling the function
          await user.getIdToken(true);

          // Get the completed goal word and type
          const goalWord = goals[matchedIndex].word;
          const goalType = goals[matchedIndex].type;

          // Ensure the parameters are properly defined
          const goalWordToSend = goalWord.trim();
          const goalTypeToSend = goalType.trim();
          const userIdToSend = user.uid;

          // Call the function to track the goal completion using our helper function
          // Pass the user ID explicitly to avoid authentication issues
          await trackGoalCompletion({
            goalWord: goalWordToSend,
            goalType: goalTypeToSend,
            userId: userIdToSend, // Pass the user ID explicitly
          });
        } else {
          console.warn('Current user object is null despite currentUser being set');
        }
      } else {
        //console.log('User not authenticated, skipping goal tracking');
      }
    } catch (error) {
      console.error('Error tracking goal completion:', error);
      // Don't show an error toast, just log it
    }

    // Show success toast
    toast({
      title: 'Goal Completed!',
      description: `You found ${droppedElement?.name}! +50 points`,
      variant: 'default', // Changed from "success" to "default" as only "default" and "destructive" are valid variants
    });

    // Trigger confetti animation for goal completion
    if (confettiRef.current) {
      const duration = 2 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        // Trigger confetti from random positions
        confettiRef.current?.fire({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        });

        confettiRef.current?.fire({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        });
      }, 250);
    }

    // Call the onSubmit callback to clear the dropped element
    onSubmit();
    setIsMatched(false);
    setMatchedIndex(null);

    // Get a new word for the matched goal type
    // Set loading state for this specific goal
    setGoals((prev) => {
      const updated = [...prev];
      updated[matchedIndex] = {
        ...updated[matchedIndex],
        word: '',
        matched: true,
      };
      return updated;
    });

    try {
      // Pass the current word and word history to ensure we get a different one
      const currentWord = newGoals[matchedIndex].word;
      const goalType = newGoals[matchedIndex].type;

      // Log the word history for debugging

      // Get a new word, excluding the current word and words in the history
      const newWord = await getRandomWord(goalType, currentWord, wordHistory[goalType]);

      // Add the new word to the history
      addToWordHistory(goalType, newWord);

      // Update with the new word
      const updatedGoals = (prev: GoalItem[]) => {
        const updated = [...prev];
        updated[matchedIndex] = {
          ...updated[matchedIndex],
          word: newWord,
          matched: false,
        };
        return updated;
      };

      setGoals(updatedGoals);

      // Update the game state goals

      const newGameStateGoals = [...gameStateGoals];

      // Make sure we preserve the goal ID
      const goalId = newGameStateGoals[matchedIndex].id;
      newGameStateGoals[matchedIndex] = {
        ...newGameStateGoals[matchedIndex],
        id: goalId, // Explicitly preserve the ID
        word: newWord,
        matched: false,
      };

      setGameStateGoals(newGameStateGoals);
    } catch (error) {
      // If there's an error, set a default word
      const defaultWord =
        newGoals[matchedIndex].type === 'Item'
          ? 'Shield'
          : newGoals[matchedIndex].type === 'Location'
          ? 'Paris'
          : 'Cleopatra';

      const updatedGoals = (prev: GoalItem[]) => {
        const updated = [...prev];
        updated[matchedIndex] = {
          ...updated[matchedIndex],
          word: defaultWord,
          matched: false,
        };
        return updated;
      };

      setGoals(updatedGoals);

      // Update the game state goals

      const newGameStateGoals = [...gameStateGoals];

      // Make sure we preserve the goal ID
      const goalId = newGameStateGoals[matchedIndex].id;
      newGameStateGoals[matchedIndex] = {
        ...newGameStateGoals[matchedIndex],
        id: goalId, // Explicitly preserve the ID
        word: defaultWord,
        matched: false,
      };

      setGameStateGoals(newGameStateGoals);
    }
  };

  // No click-to-click functionality

  return (
    <>
      <Confetti
        ref={confettiRef}
        className="fixed left-0 top-0 z-50 w-full h-full pointer-events-none"
      />
      <div
        ref={drop}
        onClick={handleTapDestination}
        className={`bg-card rounded-xl shadow-lg p-2 border border-border
          ${isOver ? 'bg-primary/5' : ''}
          ${
            mode === 'tap-to-location' && selectedElement ? 'cursor-pointer hover:bg-accent/10' : ''
          }
          ${isShaking ? 'animate-shake' : ''}
        `}
      >
        <div className="flex justify-between items-center mb-1">
          <div className="flex items-center">
            <Target className="h-4 w-4 mr-1" />
            <h3 className="text-sm font-bold">Goals</h3>
          </div>

          {isMatched && (
            <Button size="sm" className="h-6 py-0 px-2" onClick={handleSubmit}>
              <Check className="h-3 w-3 mr-1" />
              <span className="text-xs">Submit</span>
            </Button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {goals.map((goal, index) => (
            <div
              key={index}
              className={`p-1 rounded-md border ${
                matchedIndex === index
                  ? 'border-green-500 bg-green-100 dark:bg-green-900/30'
                  : goal.matched
                  ? 'border-gray-300 bg-gray-100 dark:bg-gray-800/30 opacity-50'
                  : skipMode && !goal.matched
                  ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/30'
                  : 'border-primary/30 bg-primary/5'
              }`}
              onClick={async () => {
                if (skipMode && !goal.matched && goal.word) {
                  // Set loading state for this goal
                  setGoals((prev) => {
                    const updated = [...prev];
                    updated[index] = {
                      ...updated[index],
                      word: '',
                      matched: false,
                    };
                    return updated;
                  });

                  // Get a new word for this goal
                  const newWord = await onSkipUsed(goal.type, goal.word, wordHistory[goal.type]);
                  addToWordHistory(goal.type, newWord);

                  // Update with the new word
                  setGoals((prev) => {
                    const updated = [...prev];
                    updated[index] = {
                      ...updated[index],
                      word: newWord,
                      matched: false,
                    };
                    return updated;
                  });

                  // Update the game state goals
                  if (gameStateGoals && gameStateGoals.length > 0) {
                    const newGameStateGoals = [...gameStateGoals];
                    const goalId = newGameStateGoals[index].id;
                    newGameStateGoals[index] = {
                      ...newGameStateGoals[index],
                      id: goalId,
                      word: newWord,
                      matched: false,
                    };
                    setGameStateGoals(newGameStateGoals);
                  }

                  // Show a toast
                  toast({
                    title: 'Goal Skipped!',
                    description: `You skipped the ${goal.type.toLowerCase()} goal`,
                    variant: 'warning',
                  });
                }
              }}
            >
              <div className="text-xs text-muted-foreground flex justify-between items-center">
                <span className="text-[10px]">{goal.type}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="focus:outline-none"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (skipCount > 0 && !goal.matched && goal.word) {
                            // Set loading state for this goal
                            setGoals((prev) => {
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                word: '',
                                matched: false,
                              };
                              return updated;
                            });

                            // Get a new word for this goal
                            const newWord = await onSkipUsed(
                              goal.type,
                              goal.word,
                              wordHistory[goal.type]
                            );
                            addToWordHistory(goal.type, newWord);

                            // Update with the new word
                            setGoals((prev) => {
                              const updated = [...prev];
                              updated[index] = {
                                ...updated[index],
                                word: newWord,
                                matched: false,
                              };
                              return updated;
                            });

                            // Update the game state goals
                            if (gameStateGoals && gameStateGoals.length > 0) {
                              const newGameStateGoals = [...gameStateGoals];
                              const goalId = newGameStateGoals[index].id;
                              newGameStateGoals[index] = {
                                ...newGameStateGoals[index],
                                id: goalId,
                                word: newWord,
                                matched: false,
                              };
                              setGameStateGoals(newGameStateGoals);
                            }

                            // Show a toast
                            toast({
                              title: 'Goal Skipped!',
                              description: `You skipped the ${goal.type.toLowerCase()} goal`,
                              variant: 'warning',
                            });
                          }
                        }}
                      >
                        <Trash2
                          className={`h-3 w-3 ${
                            skipCount > 0 && !goal.matched && goal.word
                              ? 'text-red-500 hover:text-red-700'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {skipCount > 0
                        ? 'Skip this goal'
                        : 'Out of skips! Complete goals to earn more.'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {isLoading || goal.word === '' ? (
                <div className="flex items-center space-x-1">
                  <Skeleton className="h-3 w-full" />
                  <Loader2 className="h-2 w-2 animate-spin" />
                </div>
              ) : (
                <div className="font-medium text-xs">{goal.word}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default GoalPanel;
